import { PROMO_SLUGS } from '@constants/promoSlugs';
import { FlaggAdmin } from 'flagg/dist/react';
import * as React from 'react';
import { Navigate, Route, Routes as RouterRoutes, useParams, useNavigate } from 'react-router-dom';

import { RouteNames } from './constants/RouteNames';
import { Page } from './Layouts/Page/Page';
import { LandingPage } from './pages/LandingPage/LandingPage'; // Keep LandingPage static for fast First Contentful Paint
import { RoomLoader } from './pages/RoomPage/RoomLoader';

const AdminRoute = React.lazy(() => import('./components/AdminRoute/AdminRoute').then(module => ({ default: module.AdminRoute })));
const UserRoute = React.lazy(() => import('./components/AdminRoute/UserRoute').then(module => ({ default: module.UserRoute })));
const MeetingLink = React.lazy(() => import('./pages/MeetingLink/MeetingLink').then(module => ({ default: module.MeetingLink })));
const MeetingSelect = React.lazy(() => import('./pages/MeetingSelect/MeetingSelect').then(module => ({ default: module.MeetingSelect })));
const PostMeeting = React.lazy(() => import('./pages/PostMeeting/PostMeeting').then(module => ({ default: module.PostMeeting })));
const PromoSlugPage = React.lazy(() => import('./pages/PromoSlugPage').then(module => ({ default: module.PromoSlugPage })));
const RoomPage = React.lazy(() => import('./pages/RoomPage/RoomPage'));
const LoginPage = React.lazy(() => import('./pages/Auth/LoginPage').then(module => ({ default: module.LoginPage })));
const SignupPage = React.lazy(() => import('./pages/Auth/SignupPage').then(module => ({ default: module.SignupPage })));
const PendingVerificationPage = React.lazy(() => import('./pages/Auth/PendingVerificationPage').then(module => ({ default: module.PendingVerificationPage })));
const GlobalConfigPage = React.lazy(() => import('./pages/Dashboard/GlobalConfigPage').then(module => ({ default: module.GlobalConfigPage })));
const JoinGroupPage = React.lazy(() => import('./pages/Public/JoinGroupPage').then(module => ({ default: module.JoinGroupPage })));
const DashboardLayout = React.lazy(() => import('./Layouts/DashboardLayout/DashboardLayout').then(module => ({ default: module.DashboardLayout })));

const LicensesPage = React.lazy(() => import('./pages/licenses/LicensesPage'));

// Wrapper component to handle roomRoute param
const RoomPageWrapper = () => {
  const { roomRoute } = useParams<{ roomRoute: string }>();
  return <RoomPage roomRoute={roomRoute || ''} />;
};

// Wrapper for FlaggAdmin to handle navigation
const FlaggAdminWrapper = () => {
  const navigate = useNavigate();
  return <FlaggAdmin onDone={() => navigate('/')} />;
};

export interface IRoutesProps { }

export const Routes: React.FC<IRoutesProps> = () => {
  return (
    <React.Suspense fallback={<Page isLoading />}>
      <RouterRoutes>
        {/* Public Pages */}
        <Route path={RouteNames.ROOT} element={<LandingPage />} />
        <Route path={RouteNames.CREATE_MEETING} element={<MeetingSelect />} />
        
        {/* Authentication */}
        <Route path={RouteNames.ENABLE_ADMIN} element={<Navigate to={RouteNames.LOGIN} replace />} />
        <Route path={RouteNames.LOGIN} element={<LoginPage />} />
        <Route path={RouteNames.SIGNUP} element={<SignupPage />} />
        <Route path="/pending-verification" element={<PendingVerificationPage />} />
        <Route path="/join/:groupId" element={<JoinGroupPage />} />

        {/* Meeting Logic */}
        <Route path={RouteNames.MEETING_LINK} element={<MeetingLink />} />
        <Route path="/:roomRoute/post_meeting" element={<PostMeeting />} />
        <Route 
          path="/:roomRoute" 
          element={
            <React.Suspense fallback={<RoomLoader />}>
              <RoomPageWrapper />
            </React.Suspense>
          } 
        />

        {/* Dashboard (Protected) */}
        <Route path="/dashboard/*" element={
          <UserRoute>
            <DashboardLayout />
          </UserRoute>
        } />

        {/* Admin */}
        <Route
          path={RouteNames.FEATURE_FLAGS}
          element={
            <AdminRoute>
              <FlaggAdminWrapper />
            </AdminRoute>
          }
        />

        {/* Misc */}
        <Route
          path={RouteNames.LICENSES}
          element={<LicensesPage />}
        />

        {PROMO_SLUGS.map((slug, index) => (
          <Route key={`${slug}_${index}`} path={`/${slug}`} element={<PromoSlugPage />} />
        ))}
      </RouterRoutes>
    </React.Suspense>
  );
};
